import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Pressable } from 'react-native'
import { OverlayProvider, Tooltip as RNTooltip } from 'react-native-popper'

import styled, { css, useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { CopyIcon } from '../../atoms/icons/CopyIcon'
import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface EligibilityCapsuleProps {
  label: string
  hasLink: boolean
  tooltip?: string
  programBookingLink?: string
  testID: string

  // Unique for multi-capsule scenario
  program?: string

  /**
   * If provided, the parent is controlling whether this capsule is selected.
   * If not provided, the capsule will manage its own "selected" state.
   */
  isSelected?: boolean

  /**
   * If provided, the parent is controlling selection. We'll call this
   * when the user presses the capsule so the parent can set "selected" externally.
   */
  onSelectProgram?: (program: string) => void
}

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: spacing['8px'],
  marginRight: '2px',
}))

const CapsuleButton = styled(Pressable)<{
  hovered: boolean
  isSelected: boolean
  hasLink: boolean
}>`
  padding: 7px 16px;
  border-radius: 100px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 32px;
  border-width: 1px;
  align-self: center;
  margin-right: 8px;
  margin-bottom: 16px;

  ${({ hovered, isSelected, theme, hasLink }) => {
    const typedTheme = theme as ThemeType

    if (isSelected && hasLink) {
      // "selected" highlight
      return css`
        border-color: ${typedTheme.colors.iconActive};
        background-color: ${typedTheme.colors.backgroundHighlightPrimary};
      `
    } else if (hovered) {
      // hovered but not selected
      return css`
        border-color: ${typedTheme.colors.borderDefault};
        background-color: ${typedTheme.colors.backgroundElement};
      `
    } else {
      // normal
      return css`
        border-color: ${typedTheme.colors.borderDefault};
        background-color: transparent;
      `
    }
  }}
`

export const EligibilityCapsule: React.FC<EligibilityCapsuleProps> = ({
  program,
  label,
  hasLink,
  tooltip,
  programBookingLink,
  isSelected,
  onSelectProgram,
  testID,
}) => {
  const theme = useTheme() as ThemeType
  const { Content, Arrow } = RNTooltip

  // Local “uncontrolled” selected state; only used if `isSelected` is NOT passed in
  const [internalSelected, setInternalSelected] = useState(false)

  // Tooltip open/close state
  const [open, setOpen] = useState(false)
  // Hover state for background styling
  const [hovered, setHovered] = useState(false)

  // Determine if we're in "controlled" mode or "uncontrolled" mode
  //  - If the parent gave us isSelected, use that.
  //  - Otherwise, use our internal localSelected state.
  const isCapsuleSelected = isSelected ?? internalSelected

  const handleHoverIn = () => {
    setHovered(true)
    setOpen(true)
  }

  const handleHoverOut = () => {
    setOpen(false)
    setHovered(false)
  }

  // Called by the Popper if the user presses ESC or clicks outside the tooltip
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
  }

  // Press => either notify parent (controlled) or set local state (uncontrolled), then copy link
  const handlePress = () => {
    if (onSelectProgram && program) {
      // "Controlled" scenario
      onSelectProgram(program)
    } else {
      // "Uncontrolled" scenario
      setInternalSelected(true)
    }

    if (programBookingLink) {
      navigator.clipboard.writeText(programBookingLink)
    }
  }

  return (
    <OverlayProvider>
      <RNTooltip
        isOpen={open}
        onOpenChange={handleOpenChange}
        on='hover'
        placement='top'
        trigger={
          <CapsuleButton
            testID={tID(testID)}
            hovered={hovered}
            isSelected={isCapsuleSelected}
            hasLink={hasLink}
            onPress={handlePress}
            onHoverIn={IS_WEB ? handleHoverIn : undefined}
            onHoverOut={IS_WEB ? handleHoverOut : undefined}
          >
            {/* Main label */}
            <BodyText size={BodyTextSize.CAPTION} color={theme.colors.textPrimary}>
              {label}
            </BodyText>

            {/* Show the copy icon if programBookingLink is not null */}
            {hasLink && (
              <IconContainer>
                <CopyIcon size={18} fillColor={theme.colors.iconActive} />
              </IconContainer>
            )}

            {/* If selected, show "Copied" text */}
            {isCapsuleSelected && hasLink && (
              <BodyText size={BodyTextSize.CAPTION} color={theme.colors.iconActive}>
                <FormattedMessage
                  defaultMessage='Copied'
                  description='Label indicating that the link has been copied to the clipboard'
                />
              </BodyText>
            )}
          </CapsuleButton>
        }
      >
        {/* Tooltip content */}
        <Content>
          <Pressable
            testID={tID(`${testID}-tooltip`)}
            style={{
              backgroundColor: 'black',
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 4,
              maxWidth: 200,
            }}
          >
            <BodyText size={BodyTextSize.SMALL} color={theme.colors.textInverse}>
              {tooltip}
            </BodyText>
          </Pressable>
          <Arrow
            style={{
              backgroundColor: 'black',
            }}
            height={8}
            width={8}
          />
        </Content>
      </RNTooltip>
    </OverlayProvider>
  )
}
